import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus, FaEllipsisV } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import ReactDOM from 'react-dom';

const ROWS_PER_PAGE = 25;

// Portal dropdown component
const DropdownPortal = ({ anchorRef, open, onClose, children }) => {
  const menuRef = useRef();
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left, // align left edge of menu with button
        zIndex: 9999,
        minWidth: 112, // 28 * 4 px
      });
    }
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div ref={menuRef} style={style} className="w-28 bg-white border border-gray-200 rounded shadow-lg">
      {children}
    </div>,
    document.body
  );
};

const ReceivedPaymentList = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const menuButtonRefs = useRef({});

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/received-payment-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch received payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Helper to format currency
  const formatCurrency = (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Approve/Reject handler
  const handleStatusChange = async (received_id, newStatus) => {
    setActionLoadingId(received_id);
    setActionMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/received-payment-post/${received_id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (res.ok) {
        setRecords(prev => prev.map(r => r.received_id === received_id ? { ...r, status: newStatus, approved_by: data.approved_by } : r));
        setActionMessage({ type: 'success', text: `Status updated to ${newStatus}` });
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to update status' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Server error' });
    }
    setActionLoadingId(null);
    setMenuOpenId(null);
  };

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">Received Payment List</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto justify-end">
          <input
            type="text"
            placeholder="Search by contract, type, status..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-[13px]"
          />
          <button
            className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
            onClick={() => navigate('/received-payment/new')}
          >
            <FaPlus className="text-[10px]" />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-[13px]">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-[13px]">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No received payment records found." />
      ) : (
        <div className="overflow-x-auto border border-gray-300 rounded-sm">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-[#f4f4f4] border-b border-gray-300">
              <tr>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Sales Contract</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Buyer Name</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Received Date</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Amount</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Payment Type</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Status</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Approved By</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Remarks</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Created At</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {records.map((item, idx) => (
                <tr key={item.received_id} className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0">
                  <td className="px-3 py-1 ">{item.sales_contract_no || 'N/A'}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.buyer_name || '-'}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{formatDate(item.received_date)}</td>
                  <td className="px-3 py-1 ">₹{formatCurrency(item.amount_received)}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">
                    <span className="">
                      {item.payment_type}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">
                    <span className={`${item.status === 'Rejected' ? ' text-red-700' : ''}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.approved_by || '-'}</td>
                  <td className="px-3 py-1 max-w-xs truncate" title={item.remarks}>
                    {item.remarks || '-'}
                  </td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{formatDate(item.created_at)}</td>
                  <td className="px-3 py-1 relative overflow-visible">
                    {item.status === 'In Process' && (
                      <>
                        <button
                          ref={el => (menuButtonRefs.current[item.received_id] = el)}
                          className="p-1 rounded hover:bg-gray-200"
                          onClick={() => setMenuOpenId(menuOpenId === item.received_id ? null : item.received_id)}
                          disabled={actionLoadingId === item.received_id}
                        >
                          <FaEllipsisV />
                        </button>
                        <DropdownPortal
                          anchorRef={{ current: menuButtonRefs.current[item.received_id] }}
                          open={menuOpenId === item.received_id}
                          onClose={() => setMenuOpenId(null)}
                        >
                          <button
                            className="block w-full text-left px-3 py-1 text-[12px]"
                            onClick={() => handleStatusChange(item.received_id, 'Approved')}
                            disabled={actionLoadingId === item.received_id}
                          >
                            {actionLoadingId === item.received_id ? 'Approving...' : 'Approve'}
                          </button>
                          <button
                            className="block w-full text-left px-3 py-1 text-red-700 hover:bg-red-50 text-[12px]"
                            onClick={() => handleStatusChange(item.received_id, 'Rejected')}
                            disabled={actionLoadingId === item.received_id}
                          >
                            {actionLoadingId === item.received_id ? 'Rejecting...' : 'Reject'}
                          </button>
                        </DropdownPortal>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionMessage && (
        <div className={`mb-2 text-center text-[13px] ${actionMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{actionMessage.text}</div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 px-2">
          <div className="text-[13px] text-gray-600">
            Showing {((page - 1) * ROWS_PER_PAGE) + 1} to {Math.min(page * ROWS_PER_PAGE, total)} of {total} records
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className="px-3 py-1 text-[13px] border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-[13px] text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className="px-3 py-1 text-[13px] border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedPaymentList;
